import { 
  FrequentlyBoughtProduct, 
  FrequentlyBoughtTogetherError, 
  ProductDto 
} from '../dto/frequentlybought.dto';
import { fbtMappingsApi, FbtMappingDto } from '../../services/fbt-mappings.api.service';
import axios from 'axios';
import { environment } from '../enviroment/environment';

const STORAGE_KEYS = {
  PRODUCTS: 'frequentlyBoughtTogether_products',
  ERRORS: 'frequentlyBoughtTogether_errors'
} as const;

class FrequentlyBoughtTogetherStorage {
  getProducts(): FrequentlyBoughtProduct[] {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEYS.PRODUCTS);
      if (!stored) return [];
      
      const products = JSON.parse(stored);
      return Array.isArray(products) ? products : [];
    } catch (error) {
      return [];
    }
  }

  addProduct(productData: ProductDto, dispatchEvent: boolean = true): void {
    try {
      const existingProducts = this.getProducts();
    
      const existingIndex = existingProducts.findIndex(p => p.id === productData._id);
      
      if (existingIndex === -1) {
        const newProduct: FrequentlyBoughtProduct = {
          id: productData._id,
          title: productData.title || productData.name || 'Unnamed Product',
          desc: productData.description || '',
          price: productData.price || 0,
          image: productData.images?.[0]?.url || '/api/placeholder/300/300',
          defaultSelected: true,
          productData: productData
        };
        
        const updatedProducts = [...existingProducts, newProduct];
        sessionStorage.setItem(STORAGE_KEYS.PRODUCTS, JSON.stringify(updatedProducts));
        if (dispatchEvent) {
          this.dispatchUpdateEvent();
        }
      }
    } catch (error) {
      this.addError('Failed to add product to frequently bought together');
    }
  }
  removeProduct(productId: string): void {
    try {
      const existingProducts = this.getProducts();
      const updatedProducts = existingProducts.filter(p => p.id !== productId);
      
      sessionStorage.setItem(STORAGE_KEYS.PRODUCTS, JSON.stringify(updatedProducts));
      this.dispatchUpdateEvent();
    } catch (error) {
      this.addError('Failed to remove product from frequently bought together');
    }
  }
  updateProductSelection(productId: string, selected: boolean): void {
    try {
      const existingProducts = this.getProducts();
      const updatedProducts = existingProducts.map(p => 
        p.id === productId ? { ...p, defaultSelected: selected } : p
      );
      
      sessionStorage.setItem(STORAGE_KEYS.PRODUCTS, JSON.stringify(updatedProducts));
      this.dispatchUpdateEvent();
    } catch (error) {
      this.addError('Failed to update product selection');
    }
  }
  clearProducts(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEYS.PRODUCTS);
      this.dispatchUpdateEvent();
    } catch (error) {
    }
  }
  getErrors(): FrequentlyBoughtTogetherError[] {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEYS.ERRORS);
      if (!stored) return [];
      
      const errors = JSON.parse(stored);
      return Array.isArray(errors) ? errors : [];
    } catch (error) {
      return [];
    }
  }
  addError(message: string, productId?: string): void {
    try {
      const existingErrors = this.getErrors();
      const newError: FrequentlyBoughtTogetherError = {
        message,
        timestamp: Date.now(),
        productId
      };
      const updatedErrors = [...existingErrors, newError].slice(-10);
      sessionStorage.setItem(STORAGE_KEYS.ERRORS, JSON.stringify(updatedErrors));
      
      this.dispatchUpdateEvent();
    } catch (error) {
    }
  }
  clearErrors(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEYS.ERRORS);
      this.dispatchUpdateEvent();
    } catch (error) {
    }
  }
  getProductCount(): number {
    return this.getProducts().length;
  }
  getSelectedProductCount(): number {
    return this.getProducts().filter(p => p.defaultSelected).length;
  }
  getSelectedProductsTotal(): number {
    return this.getProducts()
      .filter(p => p.defaultSelected)
      .reduce((total, p) => total + p.price, 0);
  }
  hasProduct(productId: string): boolean {
    return this.getProducts().some(p => p.id === productId);
  }
  getProduct(productId: string): FrequentlyBoughtProduct | null {
    return this.getProducts().find(p => p.id === productId) || null;
  }

  private dispatchUpdateEvent(): void {
    window.dispatchEvent(new CustomEvent('frequentlyBoughtTogetherUpdated'));
  }
  initializeWithProduct(productData: ProductDto): void {
    const existingProducts = this.getProducts();
    if (existingProducts.length === 0) {
      this.addProduct(productData, false); // Don't dispatch for initialization
    }
  }
  addProducts(products: ProductDto[], dispatchEvent: boolean = true): void {
    products.forEach(product => this.addProduct(product, dispatchEvent));
  }
  getStorageInfo(): {
    productCount: number;
    selectedCount: number;
    totalPrice: number;
    errorCount: number;
    storageSize: number;
  } {
    const products = this.getProducts();
    const errors = this.getErrors();
    const selectedProducts = products.filter(p => p.defaultSelected);
    
    return {
      productCount: products.length,
      selectedCount: selectedProducts.length,
      totalPrice: selectedProducts.reduce((total, p) => total + p.price, 0),
      errorCount: errors.length,
      storageSize: JSON.stringify({ products, errors }).length
    };
  }

  async getGroupedBySubcategory(): Promise<Record<string, FrequentlyBoughtProduct[]>> {
    const products = this.getProducts();
    const mappings: FbtMappingDto[] = await fbtMappingsApi.list();

    const subIdToName = new Map<string, string>();
    for (const m of mappings) {
      const name = m?.subCategory?.name || m.subCategoryId;
      if (!subIdToName.has(m.subCategoryId)) subIdToName.set(m.subCategoryId, name);
    }

    const getProductCategoryIds = (p: ProductDto): string[] => {
      const cats = p.categories || [];
      const subs = (p as any).subcategories || [];
      const allIds: string[] = [];
      
      // Add category IDs
      if (Array.isArray(cats)) {
        if (cats.length > 0) {
          if (typeof cats[0] === 'string') {
            allIds.push(...cats as string[]);
          } else {
            allIds.push(...(cats as Array<{ _id: string }>).map(c => c._id));
          }
        }
      }
      
      // Add subcategory IDs
      if (Array.isArray(subs)) {
        if (subs.length > 0) {
          if (typeof subs[0] === 'string') {
            allIds.push(...subs as string[]);
          } else {
            allIds.push(...subs.map((s: any) => typeof s === 'string' ? s : s._id || s));
          }
        }
      }
      
      return [...new Set(allIds)]; // Remove duplicates
    };

    const grouped: Record<string, FrequentlyBoughtProduct[]> = {};
    for (const prod of products) {
      const catIds = getProductCategoryIds(prod.productData);
      // Find a mapped subcategory this product belongs to
      const matched = mappings.find(m => catIds.includes(m.subCategoryId));
      if (matched) {
        const key = subIdToName.get(matched.subCategoryId) || matched.subCategoryId;
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(prod);
      }
    }
    return grouped;
  }

  async fetchPairedProducts(currentProduct: ProductDto): Promise<void> {
    try {
      const mappings: FbtMappingDto[] = await fbtMappingsApi.list();
      if (!mappings.length) return;

      // Get current product's subcategory IDs
      const getProductCategoryIds = (p: ProductDto): string[] => {
        const cats = p.categories || [];
        const subs = (p as any).subcategories || [];
        const allIds: string[] = [];
        
        // Add category IDs
        if (Array.isArray(cats)) {
          if (cats.length > 0) {
            if (typeof cats[0] === 'string') {
              allIds.push(...cats as string[]);
            } else {
              allIds.push(...(cats as Array<{ _id: string }>).map(c => c._id));
            }
          }
        }
        
        // Add subcategory IDs
        if (Array.isArray(subs)) {
          if (subs.length > 0) {
            if (typeof subs[0] === 'string') {
              allIds.push(...subs as string[]);
            } else {
              allIds.push(...subs.map((s: any) => typeof s === 'string' ? s : s._id || s));
            }
          }
        }
        
        return [...new Set(allIds)]; // Remove duplicates
      };

      const currentCatIds = getProductCategoryIds(currentProduct);
      console.log('Current product categories:', currentCatIds);
      console.log('Available mappings:', mappings);
      if (!currentCatIds.length) return;

      // Find paired subcategories - bidirectional
      const pairedSubIds = new Set<string>();
      for (const mapping of mappings) {
        if (currentCatIds.includes(mapping.parentCategoryId)) {
          // Current product is in the parent category, add the sub
          pairedSubIds.add(mapping.subCategoryId);
        }
        if (currentCatIds.includes(mapping.subCategoryId)) {
          // Current product is in the sub category, add the parent
          pairedSubIds.add(mapping.parentCategoryId);
        }
      }

      console.log('Paired subcategory IDs before filtering:', Array.from(pairedSubIds));
      
      // Remove current product's categories from paired
      const filteredPairedIds = Array.from(pairedSubIds).filter(id => !currentCatIds.includes(id));
      console.log('Filtered paired IDs:', filteredPairedIds);
      
      if (!filteredPairedIds.length) return;

      // Fetch products from paired subcategories
      const url = `${environment.api.apiRootServer}/products`;
      const params = { subcategories: filteredPairedIds.join(','), limit: 10 };
      console.log('Fetching products with params:', params);
      const response = await axios.get(url, { params });

      if (response.data?.success && response.data?.data) {
        const pairedProducts = response.data.data.filter((p: any) => p._id !== currentProduct._id);
        console.log('Fetched paired products:', pairedProducts.map((p: any) => ({ id: p._id, title: p.title, categories: p.categories })));
        this.addProducts(pairedProducts, false); // Don't dispatch events for paired products
      }
    } catch (error) {
      console.error('Error in fetchPairedProducts:', error);
    }
  }
}
export const frequentlyBoughtTogetherStorage = new FrequentlyBoughtTogetherStorage();
export { FrequentlyBoughtTogetherStorage };
export type {
  FrequentlyBoughtProduct,
  FrequentlyBoughtTogetherError,
  ProductDto
} from '../dto/frequentlybought.dto';
